/**
 * Auth0 password strength level. Controls which validation rules are shown
 * in the registration form. Defaults to "good".
 */
export type PasswordStrengthLevel =
  | "none"
  | "low"
  | "fair"
  | "good"
  | "excellent";

const VALID_LEVELS = new Set<string>([
  "none",
  "low",
  "fair",
  "good",
  "excellent",
]);

/**
 * Parses and validates a password strength level string.
 * Returns the default level ("good") if the value is invalid or undefined.
 */
export const parsePasswordStrengthLevel = (
  value: string | undefined,
): PasswordStrengthLevel =>
  value && VALID_LEVELS.has(value) ? (value as PasswordStrengthLevel) : "good";

/**
 * A legal link displayed on auth pages (e.g. privacy statement, terms).
 */
export type AuthLegalLink = {
  /**
   * i18n key for the link label (e.g. "authScreens.legalLinks.privacyStatement")
   */
  labelKey: string;
  /**
   * URL the link points to
   */
  url: string;
};

/**
 * Configuration for the auth package.
 * Provided via AuthConfigProvider at the app level.
 */
export type AuthConfig = {
  /**
   * Auth0 configuration for WebAuth and SPA SDK clients
   */
  auth0: {
    /**
     * Auth0 tenant domain (e.g. "auth-development.huub.nl")
     */
    domain: string;
    /**
     * Auth0 application client ID
     */
    clientId: string;
    /**
     * Redirect URI for OAuth2 callback (e.g. "http://localhost:5173/auth/callback")
     */
    redirectUri: string;
    /**
     * Auth0 API audience (e.g. "http://localhost:8000")
     */
    audience: string;
  };
  /**
   * API endpoints used by auth flows
   */
  api: {
    /**
     * POST endpoint to verify tokens with the backend
     */
    verifyTokenEndpoint: string;
  };
  /**
   * Cookie configuration for cross-domain token sharing
   */
  cookie: {
    /**
     * Cookie name for storing the identity token
     */
    name: string;
    /**
     * Cookie domain (empty for localhost, ".huub.nl" for production)
     */
    domain?: string;
  };
  /**
   * Auth0 password strength level. Controls which validation rules are shown
   * in the registration form. Defaults to "good".
   */
  passwordStrengthLevel?: PasswordStrengthLevel;
  /**
   * Optional legal links shown on login and register pages.
   * Tenant-specific (e.g. Eigen Huis privacy statement, terms).
   */
  legalLinks?: AuthLegalLink[];
  /**
   * Optional i18n key of a statutory notice shown under the legal links
   * (e.g. the identification of the licence holder).
   */
  legalNotice?: string;
};
